import { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  QueryConstraint,
  DocumentData,
  QuerySnapshot,
  startAfter,
  DocumentSnapshot,
  getDocs,
  where,
} from "firebase/firestore";
import { db } from "../config/firebase";

interface UseFirestoreRealtimeOptions {
  collectionName: string;
  orderByField?: string;
  orderDirection?: "asc" | "desc";
  limitCount?: number;
  additionalConstraints?: QueryConstraint[];
  enableDebugLogging?: boolean;
  // New pagination options
  enablePagination?: boolean;
  pageSize?: number;
  enableVirtualization?: boolean;
}

interface UseFirestoreRealtimeReturn<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  connectionStatus: "connecting" | "connected" | "error";
  documentCount: number;
  // New pagination properties
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentPage: number;
  totalPages: number;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
}

// New interface for paginated data management
interface PaginatedData<T> {
  pages: Map<number, T[]>;
  cursors: Map<number, DocumentSnapshot>;
  totalCount?: number;
}

export function useFirestoreRealtime<T = DocumentData>({
  collectionName,
  orderByField = "timestamp",
  orderDirection = "desc",
  limitCount = 100,
  additionalConstraints = [],
  enableDebugLogging = false,
  enablePagination = false,
  pageSize = 25,
  enableVirtualization = false,
}: UseFirestoreRealtimeOptions): UseFirestoreRealtimeReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    "connecting" | "connected" | "error"
  >("connecting");
  const [documentCount, setDocumentCount] = useState(0);

  // Pagination state
  const [paginatedData, setPaginatedData] = useState<PaginatedData<T>>({
    pages: new Map(),
    cursors: new Map(),
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Effective page size and limit
  const effectivePageSize = enablePagination ? pageSize : limitCount;
  const effectiveLimit = enablePagination ? pageSize : limitCount;

  // Load a specific page
  const loadPage = async (pageNumber: number, cursor?: DocumentSnapshot) => {
    if (enableDebugLogging) {
      console.log(
        `📄 Loading page ${pageNumber} for collection: ${collectionName}`
      );
    }

    try {
      setLoading(true);

      const constraints: QueryConstraint[] = [
        orderBy(orderByField, orderDirection),
        limit(effectiveLimit + 1), // +1 to check if there's a next page
        ...additionalConstraints,
      ];

      if (cursor) {
        constraints.splice(1, 0, startAfter(cursor));
      }

      const q = query(collection(db, collectionName), ...constraints);
      const snapshot = await getDocs(q);

      const documents: T[] = [];
      const docs = snapshot.docs;

      // Check if there are more pages
      const hasMore = docs.length > effectiveLimit;
      const pageData = hasMore ? docs.slice(0, effectiveLimit) : docs;

      pageData.forEach((doc) => {
        documents.push({
          id: doc.id,
          ...doc.data(),
          _lastFetched: Date.now(),
        } as T);
      });

      // Update paginated data
      setPaginatedData((prev) => {
        const newPages = new Map(prev.pages);
        const newCursors = new Map(prev.cursors);

        newPages.set(pageNumber, documents);

        if (hasMore && pageData.length > 0) {
          newCursors.set(pageNumber, pageData[pageData.length - 1]);
        }

        return {
          pages: newPages,
          cursors: newCursors,
          totalCount: prev.totalCount,
        };
      });

      setHasNextPage(hasMore);
      setHasPreviousPage(pageNumber > 0);
      setCurrentPage(pageNumber);
      setData(documents);
      setDocumentCount(documents.length);
      setLastUpdated(new Date());
      setLoading(false);

      if (enableDebugLogging) {
        console.log(
          `✅ Loaded page ${pageNumber}: ${documents.length} documents`
        );
      }
    } catch (err) {
      console.error(`❌ Error loading page ${pageNumber}:`, err);
      setError(err instanceof Error ? err.message : "Unknown error");
      setLoading(false);
    }
  };

  const loadNextPage = async () => {
    if (!hasNextPage) return;

    const cursor = paginatedData.cursors.get(currentPage);
    await loadPage(currentPage + 1, cursor);
  };

  const loadPreviousPage = async () => {
    if (!hasPreviousPage) return;

    // For previous page, we need to implement reverse pagination
    // This is more complex in Firestore, so we'll cache pages
    const prevPageData = paginatedData.pages.get(currentPage - 1);
    if (prevPageData) {
      setData(prevPageData);
      setCurrentPage(currentPage - 1);
      setHasNextPage(true);
      setHasPreviousPage(currentPage - 1 > 0);
    }
  };

  const goToPage = async (pageNumber: number) => {
    if (pageNumber < 0) return;

    const cachedPage = paginatedData.pages.get(pageNumber);
    if (cachedPage) {
      setData(cachedPage);
      setCurrentPage(pageNumber);
      return;
    }

    // For non-cached pages, we'd need to implement more complex navigation
    // For now, only allow sequential navigation
    if (Math.abs(pageNumber - currentPage) === 1) {
      if (pageNumber > currentPage) {
        await loadNextPage();
      } else {
        await loadPreviousPage();
      }
    }
  };

  const refresh = async () => {
    setPaginatedData({ pages: new Map(), cursors: new Map() });
    await loadPage(0);
  };

  useEffect(() => {
    if (enableDebugLogging) {
      console.log(
        `🔥 Setting up Firestore listener for collection: ${collectionName}`
      );
      console.log(`📊 Query params:`, {
        orderByField,
        orderDirection,
        limitCount: effectiveLimit,
        enablePagination,
        pageSize: effectivePageSize,
      });
    }

    setLoading(true);
    setError(null);
    setConnectionStatus("connecting");

    if (enablePagination) {
      // Use paginated loading instead of real-time listener for large datasets
      loadPage(0).then(() => {
        setConnectionStatus("connected");
      });
      return;
    }

    try {
      // Build the query for real-time updates (non-paginated)
      const constraints: QueryConstraint[] = [
        orderBy(orderByField, orderDirection),
        limit(effectiveLimit),
        ...additionalConstraints,
      ];

      const q = query(collection(db, collectionName), ...constraints);

      if (enableDebugLogging) {
        console.log(`🔍 Query built successfully for ${collectionName}`);
      }

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot) => {
          if (enableDebugLogging) {
            console.log(`📥 Received snapshot for ${collectionName}:`, {
              size: snapshot.size,
              empty: snapshot.empty,
              hasPendingWrites: snapshot.metadata.hasPendingWrites,
              fromCache: snapshot.metadata.fromCache,
            });

            // Log document changes for debugging
            snapshot.docChanges().forEach((change) => {
              if (change.type === "added") {
                console.log(
                  `➕ New document added:`,
                  change.doc.id,
                  change.doc.data()
                );
              }
              if (change.type === "modified") {
                console.log(
                  `🔄 Document modified:`,
                  change.doc.id,
                  change.doc.data()
                );
              }
              if (change.type === "removed") {
                console.log(`➖ Document removed:`, change.doc.id);
              }
            });
          }

          const documents: T[] = [];
          snapshot.forEach((doc) => {
            const docData = {
              id: doc.id,
              ...doc.data(),
              // Add a timestamp to force re-render detection
              _lastFetched: Date.now(),
            } as T;

            if (enableDebugLogging && documents.length < 3) {
              console.log(
                `📄 Sample document ${documents.length + 1}:`,
                docData
              );
            }

            documents.push(docData);
          });

          if (enableDebugLogging) {
            console.log(
              `✅ Successfully processed ${documents.length} documents from ${collectionName}`
            );

            // Log changes summary
            const changes = snapshot.docChanges();
            if (changes.length > 0) {
              console.log(`🔄 Changes detected:`, {
                added: changes.filter((c) => c.type === "added").length,
                modified: changes.filter((c) => c.type === "modified").length,
                removed: changes.filter((c) => c.type === "removed").length,
              });
            }
          }

          setData(documents);
          setDocumentCount(documents.length);
          setLastUpdated(new Date());
          setLoading(false);
          setConnectionStatus("connected");
        },
        (error) => {
          console.error(
            `❌ Firestore listener error for ${collectionName}:`,
            error
          );
          console.error(`🔧 Error details:`, {
            code: error.code,
            message: error.message,
            stack: error.stack,
          });

          setError(`${error.code}: ${error.message}`);
          setLoading(false);
          setConnectionStatus("error");
        }
      );

      if (enableDebugLogging) {
        console.log(`🎯 Listener attached successfully for ${collectionName}`);
      }

      // Cleanup function
      return () => {
        if (enableDebugLogging) {
          console.log(`🧹 Cleaning up listener for ${collectionName}`);
        }
        unsubscribe();
      };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      console.error(`💥 Failed to set up listener for ${collectionName}:`, err);

      setError(errorMessage);
      setLoading(false);
      setConnectionStatus("error");
    }
  }, [
    collectionName,
    orderByField,
    orderDirection,
    effectiveLimit,
    JSON.stringify(additionalConstraints),
    enableDebugLogging,
    enablePagination,
    effectivePageSize,
  ]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    connectionStatus,
    documentCount,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    totalPages,
    loadNextPage,
    loadPreviousPage,
    goToPage,
    refresh,
  };
}

// Specialized hook for health metrics
export function useHealthMetricsRealtime(collectionName: string) {
  return useFirestoreRealtime({
    collectionName,
    orderByField: "timestamp",
    orderDirection: "desc",
    limitCount: 10, // Get last 10 metrics
    enableDebugLogging: true, // Enable debug logging for health metrics
  });
}

// Specialized hook for audit issues
export function useAuditIssuesRealtime(
  collectionName: string,
  limitCount = 100
) {
  return useFirestoreRealtime({
    collectionName,
    orderByField: "timestamp",
    orderDirection: "desc",
    limitCount,
  });
}

// Add type definition for musicVideoAssetEmbeddings
export interface MusicVideoAssetEmbedding {
  id?: string;
  musicVideoId: string;
  soundRecordingId?: string;
  videoId?: string;
  isrc?: string;
  artist?: string;
  label?: string;
  customId?: string;
  updatedAt?: Date;
}

// Specialized hook for music video asset embeddings
export function useMusicVideoEmbeddingsRealtime(limitCount = 1000) {
  return useFirestoreRealtime<MusicVideoAssetEmbedding>({
    collectionName: "musicVideoAssetEmbeddings",
    orderByField: "updatedAt",
    orderDirection: "desc",
    limitCount,
    enableDebugLogging: true, // Enable debug logging for embeddings
  });
}

// Enhanced hook specifically for music video embeddings with change tracking
export function useMusicVideoEmbeddingsRealtimeEnhanced(limitCount = 1000) {
  const [changeCount, setChangeCount] = useState(0);
  const [lastChangeType, setLastChangeType] = useState<
    "added" | "modified" | "removed" | null
  >(null);

  const result = useFirestoreRealtime<MusicVideoAssetEmbedding>({
    collectionName: "musicVideoAssetEmbeddings",
    orderByField: "updatedAt",
    orderDirection: "desc",
    limitCount,
    enableDebugLogging: true,
  });

  // Track changes by watching the lastUpdated timestamp
  useEffect(() => {
    if (result.lastUpdated) {
      setChangeCount((prev) => prev + 1);
      console.log(
        `🔄 Change detected in musicVideoAssetEmbeddings (change #${
          changeCount + 1
        })`
      );
    }
  }, [result.lastUpdated]);

  return {
    ...result,
    changeCount,
    lastChangeType,
  };
}
